import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Target, 
  TrendingUp, 
  Rocket,
  PlayCircle,
  Coffee,
  Globe,
  Search,
  FileText,
  Database,
  Landmark,
  Hash,
  ListTree,
  Calendar,
  FolderOpen,
  Mail,
  Zap,
  Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import PlaybooksPreview from '../components/landing/PlaybooksPreview';
import CoffeeComparison from '../components/landing/CoffeeComparison';
import SocialProof from '../components/landing/SocialProof';
import { SEOHead, SITE_ORIGIN } from '@/components/seo/SEOHead';

/**
 * Hero trust strip: photo-only headshots (Unsplash, face-cropped), typical US/EU professional stock look.
 * Single light edge on the pill; no extra ring so stacks read like common SaaS avatar rows.
 */
const HERO_TRUST_FACE_URLS = [
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1519085360753-af0119f7c76e?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1519345182560-a3d06b774a94?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1500048993953-d23a436266cf?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1463453091185-61582044d556?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
  'https://images.unsplash.com/photo-1539577716330-0a2bdbcbf299?w=128&h=128&fit=crop&crop=faces&q=80&auto=format',
] as const;

const PublicHomepage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Redirect authenticated users to their playbooks
  useEffect(() => {
    if (user) {
      navigate('/playbooks');
    }
  }, [user, navigate]);

  // Track mouse for interactive effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // If user is authenticated, show loading while redirecting
  if (user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-zinc-600 dark:text-zinc-400">Redirecting to your playbooks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <SEOHead
        title="LaunchGuide.io - Marketing Platform for Technical Founders & Developers"
        description="LaunchGuide helps technical founders and developers go from 0 to $10k MRR with a step-by-step marketing plan and AI-powered marketing agents."
        pathname="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'LaunchGuide.io',
          url: SITE_ORIGIN,
        }}
      />
      <Navbar />
      
      {/* Hero Section - World-Class Design */}
      <section className="relative isolate py-16 md:py-24 lg:py-32 overflow-hidden">
        {/* Interactive Background Effects (z-0 keeps layers above page bg; isolate contains stacking) */}
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${JSON.stringify(`${import.meta.env.BASE_URL}hero-night.png`)})`,
            }}
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-gradient-to-b from-background/35 via-background/18 to-background/42 dark:from-zinc-950/40 dark:via-zinc-950/22 dark:to-zinc-950/48"
            aria-hidden
          />
          {/* Primary gradient */}
          <div className="absolute inset-0" style={{
            background: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(99, 102, 241, 0.1) 30%, rgba(255, 255, 255, 0) 70%)"
          }}></div>
          
          {/* Interactive mouse spotlight */}
          <motion.div 
            className="absolute w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(59, 130, 246, 0.3) 0%, transparent 70%)",
              left: mousePosition.x - 300,
              top: mousePosition.y - 300,
            }}
            animate={{
              left: mousePosition.x - 300,
              top: mousePosition.y - 300,
            }}
            transition={{
              type: "spring",
              damping: 20,
              stiffness: 100,
            }}
          />
          
          {/* Floating geometric shapes */}
          <motion.div 
            className="absolute top-[15%] right-[10%] w-72 h-72 rounded-full bg-gradient-to-r from-blue-400/20 to-blue-400/20 blur-3xl" 
            animate={{
              y: [0, 40, 0],
              scale: [1, 1.1, 1],
            }} 
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }} 
          />
          
          <motion.div 
            className="absolute bottom-[10%] left-[5%] w-80 h-80 rounded-full bg-gradient-to-r from-blue-400/15 to-blue-400/15 blur-3xl" 
            animate={{
              y: [0, -30, 0],
              scale: [1, 1.05, 1],
            }} 
            transition={{
              duration: 18,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }} 
          />
        </div>

        {/* Ease the cut into the playbooks block (solid white / zinc-900 below) */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-36 bg-gradient-to-b from-transparent via-white/50 to-white sm:h-44 md:h-52 dark:via-zinc-900/55 dark:to-zinc-900"
          aria-hidden
        />

        <div className="container relative z-10 px-4 md:px-6 max-w-7xl mx-auto">
          <motion.div 
            className="text-center space-y-8 max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.2 }
              }
            }}
          >
            {/* Trust Badge - stacked on mobile for readable wrap */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
              }}
              className="inline-flex max-w-full flex-col items-center gap-2.5 px-4 py-3 sm:flex-row sm:gap-3 sm:py-2 rounded-full bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-950/50 dark:to-blue-950/50 border border-blue-200/50 dark:border-blue-800/50"
            >
              <div
                className="flex shrink-0 items-center justify-center pl-2"
                aria-hidden
              >
                {HERO_TRUST_FACE_URLS.map((src, index) => (
                  <div
                    key={src}
                    className="relative -ml-4 h-9 w-9 first:ml-0 sm:-ml-[18px] sm:h-10 sm:w-10"
                    style={{ zIndex: HERO_TRUST_FACE_URLS.length - index }}
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-full w-full rounded-full border-2 border-white object-cover object-top dark:border-zinc-950"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const fallback = `https://randomuser.me/api/portraits/men/${(index * 7 + 13) % 99}.jpg`;
                        if (e.currentTarget.src !== fallback) {
                          e.currentTarget.src = fallback;
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <span className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300 text-center sm:text-left leading-snug">
                Trusted by 436 founders
              </span>
            </motion.div>
            
            {/* Main Headline - Conversion Focused */}
            <motion.h1 
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
              }}
              className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.15]"
            >
              <span className="block text-zinc-900 dark:text-zinc-100">
                Take Your Product From
              </span>
              <span className="block bg-gradient-to-r gradient-text">
                0 to $10K MRR
              </span>
              <span className="block text-zinc-900 dark:text-zinc-100 text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-2">
                With Proven Playbooks
              </span>
            </motion.h1>
            
            {/* Value Proposition */}
            <motion.p 
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
              }}
              className="text-base sm:text-lg md:text-xl lg:text-2xl text-zinc-600 dark:text-zinc-300 max-w-3xl mx-auto leading-relaxed px-1"
            >
              The exact step-by-step playbooks and AI tools that get you to $10K MRR. No guesswork. One subscription.
            </motion.p>
            
            {/* Key Benefits */}
            {/* Primary CTA */}
            <motion.div 
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
              }}
              className="space-y-4"
            >
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200 group w-full sm:w-auto"
              >
                <Link to="/auth/register">
                  Start Growing
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform shrink-0" />
                </Link>
              </Button>
              
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
                Full toolkit. Cancel anytime.
              </p>
              
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3 sm:gap-6 text-zinc-600 dark:text-zinc-300">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                  <span className="font-medium text-sm sm:text-base">Playbooks that map the path</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                  <span className="font-medium text-sm sm:text-base">13+ AI tools in one place</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                  <span className="font-medium text-sm sm:text-base">Cancel anytime</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Playbooks Preview Section */}
      <PlaybooksPreview />

      {/* AI Tools Section */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-zinc-50/90 dark:bg-zinc-900/80" />
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.03)_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_70%_50%_at_50%_0%,#000_60%,transparent_110%)]" />
        <div className="container px-4 md:px-6 relative max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-12 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="secondary" className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground border-zinc-200 dark:border-zinc-700 rounded-full px-4 py-1">
              Your toolkit
            </Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 mb-4">
              All the <span className="gradient-text">AI tools</span> you need
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Stop paying for multiple AI tools. One subscription gives you the full toolkit to research your market, create content that converts, and launch your project.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.08, delayChildren: 0.1 },
              },
            }}
          >
            {/* Research */}
            <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
                  <Database className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Research</h3>
              </div>
              <div className="space-y-3">
                {[
                  { icon: Globe, title: 'Analyze Website', desc: 'Uncover content angles and positioning from any URL.' },
                  { icon: Search, title: 'Research Results', desc: 'Product, audience, and keyword insights in one place.' },
                  { icon: FileText, title: 'Product research', desc: 'Messaging and positioning tailored to your product.' },
                  { icon: Users, title: 'Audience research', desc: 'Ideal customer profiles and where they hang out.' },
                  { icon: Hash, title: 'Keywords', desc: 'Keyword opportunities and content gaps.' },
                  { icon: Landmark, title: 'Marketing Vault', desc: 'Curated playbooks and templates.' },
                ].map((tool, i) => (
                  <Card key={i} className="group border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/50 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                        <tool.icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <h4 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm md:text-base">{tool.title}</h4>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">{tool.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Content Engine */}
            <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
                  <Zap className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Content Engine</h3>
              </div>
              <div className="space-y-3">
                {[
                  { icon: ListTree, title: 'Generate Content', desc: 'AI content ideas and briefs from your research.' },
                  { icon: FileText, title: 'Content Library', desc: 'All your generated content in one place.' },
                  { icon: Calendar, title: 'Social Calendar', desc: 'Plan and schedule posts across platforms.' },
                ].map((tool, i) => (
                  <Card key={i} className="group border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/50 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                        <tool.icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <h4 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm md:text-base">{tool.title}</h4>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">{tool.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>

            {/* Launch tools */}
            <motion.div className="space-y-4" variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
                  <Rocket className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Launch tools</h3>
              </div>
              <div className="space-y-3">
                {[
                  { icon: FolderOpen, title: 'Directories', desc: 'Submit to launch directories in one flow.' },
                  { icon: Target, title: 'Product Hunt', desc: 'Generate launch content and prepare for launch day.' },
                  { icon: Mail, title: 'Cold Outreach', desc: 'Email templates and outreach strategies.' },
                  { icon: Lightbulb, title: 'Competitor content', desc: 'Recreate and adapt competitor content.' },
                ].map((tool, i) => (
                  <Card key={i} className="group border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-800/50 shadow-sm hover:shadow-md hover:border-primary/20 transition-all duration-200 rounded-xl overflow-hidden">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
                        <tool.icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <h4 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm md:text-base">{tool.title}</h4>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">{tool.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-12 md:mt-14 text-center space-y-4"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <p className="text-zinc-600 dark:text-zinc-400 text-base md:text-lg max-w-xl mx-auto">
              Use them to find your audience, write what resonates, and launch where it matters. One subscription, one toolkit, so you can focus on building.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl h-11 sm:h-12 px-6 sm:px-8 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 group w-full sm:w-auto"
            >
              <Link to="/auth/register">
                Get the full toolkit for €29/month
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform shrink-0" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Coffee Price Comparison */}
      <CoffeeComparison />

      {/* Social Proof Section */}
      <SocialProof />

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-zinc-50 to-blue-50 dark:from-zinc-900 dark:to-blue-950/20">
        <div className="container px-4 md:px-6 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-zinc-900 dark:text-zinc-100">
              Get the playbooks and tools. Hit $10K MRR.
            </h2>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-600 dark:text-zinc-300">
              Everything you need in one place. €29/month. Cancel anytime.
            </p>
            
            <Button 
              asChild
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-5 sm:px-12 sm:py-6 text-base sm:text-lg md:text-xl font-semibold rounded-xl shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200 group w-full sm:w-auto"
            >
              <Link to="/auth/register">
                Get access for €29/month
                <Rocket className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            
            <div className="pt-8 border-t border-zinc-200 dark:border-zinc-700">
              <p className="text-sm text-zinc-500 dark:text-zinc-400 flex items-center justify-center gap-2">
                <Target className="h-4 w-4" />
                Playbooks + 13 AI tools. Cancel anytime.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PublicHomepage; 